/**
 * INT-0026 — Upload Schedule dialog on the division page. Flow: pick a file
 * (image/PDF/CSV/XLSX) → upload to Spaces → AI parse job (BullMQ, polled) →
 * EDITABLE preview (teams + games + not-imported rows) → Confirm commits.
 * Nothing is saved until the organizer confirms on the preview screen. The
 * editable teams/games preview itself is the shared ScheduleDraftEditor
 * (also used by the create wizard, INT-0051).
 */
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/auth/clerkShim';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import useScheduleImportStore from '@store/scheduleImportStore';
import ScheduleDraftEditor from '@/components/ScheduleImport/ScheduleDraftEditor';
import { importableGames, validateDraft } from './schedulePreview';

const ACCEPT = 'image/*,application/pdf,.csv,.xlsx,.xls,.doc,.docx';

type Phase = 'pick' | 'uploading' | 'parsing' | 'preview' | 'committing';

export default function ScheduleUploadDialog({
  open,
  onOpenChange,
  divisionId,
  divisionName,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  divisionId: string;
  divisionName?: string;
  onImported: () => void;
}) {
  const { getToken } = useAuth();
  const { uploadSchedule, parseSchedule, pollScheduleJob, commitSchedule } =
    useScheduleImportStore((s: any) => ({
      uploadSchedule: s.uploadSchedule,
      parseSchedule: s.parseSchedule,
      pollScheduleJob: s.pollScheduleJob,
      commitSchedule: s.commitSchedule,
    }));

  const [phase, setPhase] = useState<Phase>('pick');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<any>(null);
  const [otherRows, setOtherRows] = useState<any[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [showOther, setShowOther] = useState(false);
  // Wrong-division check (owner 2026-09-13): when the document claims a
  // different division, Import stays disabled until this explicit
  // confirmation — the organizer may well mean it (similar names), but it
  // must be a decision, not an accident. Nothing here renames the division.
  const [documentDivision, setDocumentDivision] = useState<string | null>(null);
  const [divisionMismatch, setDivisionMismatch] = useState(false);
  const [mismatchConfirmed, setMismatchConfirmed] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setPhase('pick');
    setError('');
    setDraft(null);
    setOtherRows([]);
    setNotes([]);
    setShowOther(false);
    setDocumentDivision(null);
    setDivisionMismatch(false);
    setMismatchConfirmed(false);
  };

  const close = (next: boolean) => {
    // Cancel/close at the preview discards everything (spec req 7).
    if (!next) reset();
    onOpenChange(next);
  };

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setError('');
    setPhase('uploading');
    try {
      const token = await getToken();
      const uploaded = await uploadSchedule(file, divisionId, token);
      setPhase('parsing');
      const { jobId } = await parseSchedule(divisionId, {
        fileUrl: uploaded.url,
        mimeType: uploaded.mimeType,
        fileName: uploaded.fileName,
      }, token);
      const result = await pollScheduleJob(jobId, token);
      if (!result || (!result.teams?.length && !result.games?.length)) {
        setPhase('pick');
        setError(
          result?.notes?.length
            ? `No teams or games found. ${result.notes.join(' ')}`
            : 'No teams or games were found in this file. Try a clearer photo or a different file.'
        );
        return;
      }
      setDraft({ teams: result.teams || [], games: result.games || [] });
      setOtherRows(result.otherDivisionRows || []);
      setNotes(result.notes || []);
      setDocumentDivision(result.documentDivision || null);
      setDivisionMismatch(!!result.divisionMismatch);
      setMismatchConfirmed(false);
      setPhase('preview');
    } catch (e: any) {
      console.error('schedule import failed', e);
      setPhase('pick');
      setError(e?.response?.data?.error || e?.message || "Couldn't read this file");
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const errors = useMemo(() => (draft ? validateDraft(draft) : []), [draft]);
  const errorByGame = useMemo(() => {
    const m = new Map<number, string>();
    for (const e of errors) if (e.gameIndex >= 0 && !m.has(e.gameIndex)) m.set(e.gameIndex, e.message);
    return m;
  }, [errors]);
  const importGames = draft ? importableGames(draft) : [];

  const onCommit = async () => {
    if (!draft || errors.length || (divisionMismatch && !mismatchConfirmed)) return;
    setPhase('committing');
    try {
      const token = await getToken();
      const result = await commitSchedule(divisionId, {
        teams: draft.teams.map((t: any) => ({ name: t.name.trim(), matchedTeamId: t.matchedTeamId || null })),
        games: importGames.map((g: any) => ({
          homeTeam: g.homePlaceholder ? '' : g.homeTeam,
          awayTeam: g.awayPlaceholder ? '' : g.awayTeam,
          homePlaceholder: g.homePlaceholder || null,
          awayPlaceholder: g.awayPlaceholder || null,
          playoffRound: g.playoffRound || null,
          date: g.date,
          time: g.time || null,
          venue: g.venue || null,
          homeScore: g.homeScore ?? null,
          awayScore: g.awayScore ?? null,
        })),
      }, token);
      toast.success(
        `Imported ${result.createdGames} game${result.createdGames === 1 ? '' : 's'} and ${result.createdTeams} new team${result.createdTeams === 1 ? '' : 's'}` +
        (result.skippedDuplicates ? ` (${result.skippedDuplicates} duplicate${result.skippedDuplicates === 1 ? '' : 's'} skipped)` : '')
      );
      reset();
      onOpenChange(false);
      onImported();
    } catch (e: any) {
      console.error('schedule commit failed', e);
      setPhase('preview');
      toast.error(e?.response?.data?.error || e?.message || 'Import failed. Nothing was saved');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className={phase === 'preview' || phase === 'committing' ? 'max-w-5xl' : 'sm:max-w-[480px]'}>
        <DialogHeader>
          <DialogTitle>Upload Schedule</DialogTitle>
          <DialogDescription>
            {phase === 'preview' || phase === 'committing'
              ? 'Review everything below. Nothing is saved until you confirm.'
              : 'Upload any schedule (a photo, PDF, Word doc, or spreadsheet) and AI will extract the teams and games for this division.'}
          </DialogDescription>
        </DialogHeader>

        {(phase === 'pick' || phase === 'uploading' || phase === 'parsing') && (
          <div className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {phase === 'pick' ? (
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-8 text-center hover:bg-muted/50">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">Choose a schedule file</span>
                <span className="text-xs text-muted-foreground">Photo, screenshot, PDF, CSV, Excel, or Word (up to 20&nbsp;MB)</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  data-testid="schedule-file-input"
                  onChange={(e) => onPickFile(e.target.files?.[0] || null)}
                />
              </label>
            ) : (
              <div className="flex items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                {phase === 'uploading' ? 'Uploading…' : 'Reading your schedule. This can take up to a minute…'}
              </div>
            )}
          </div>
        )}

        {(phase === 'preview' || phase === 'committing') && draft && (
          <div className="max-h-[65vh] space-y-6 overflow-y-auto pr-1">
            {divisionMismatch && (
              <div className="rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  <div className="space-y-2">
                    <p>
                      This schedule says it&apos;s for <strong>{documentDivision}</strong>, but
                      you&apos;re importing into <strong>{divisionName || 'this division'}</strong>.
                      If that&apos;s intentional (similar names, renamed division), confirm below:
                      the teams and games will be created here, and the division&apos;s name will
                      NOT be changed.
                    </p>
                    <label className="flex cursor-pointer items-center gap-2 font-medium">
                      <input
                        type="checkbox"
                        checked={mismatchConfirmed}
                        onChange={(e) => setMismatchConfirmed(e.target.checked)}
                        data-testid="schedule-mismatch-confirm"
                      />
                      <span>Yes, import into {divisionName || 'this division'} anyway</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
            {notes.length > 0 && (
              <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
                {notes.map((n, i) => <div key={i}>• {n}</div>)}
              </div>
            )}

            <ScheduleDraftEditor draft={draft} onChange={setDraft} errorByGame={errorByGame} />

            {/* Rows filtered to other divisions — visible, never silent (grilling #4). */}
            {otherRows.length > 0 && (
              <section>
                <button
                  type="button"
                  className="text-sm font-medium text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => setShowOther((v) => !v)}
                >
                  Not imported: other divisions ({otherRows.length}) {showOther ? '▾' : '▸'}
                </button>
                {showOther && (
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {otherRows.map((r: any, i: number) => (
                      <li key={i}>• <span className="font-medium">{r.division}:</span> {r.description}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
        )}

        {(phase === 'preview' || phase === 'committing') && (
          <DialogFooter className="items-center gap-2">
            {errors.length > 0 && (
              <span className="mr-auto text-sm text-destructive">
                Fix {errors.length} highlighted row{errors.length === 1 ? '' : 's'} (or remove them) to import.
              </span>
            )}
            <Button variant="outline" onClick={() => close(false)} disabled={phase === 'committing'}>
              Cancel
            </Button>
            <Button onClick={onCommit} disabled={phase === 'committing' || errors.length > 0 || (divisionMismatch && !mismatchConfirmed) || (!draft?.teams.length && !importGames.length)}>
              {phase === 'committing' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing…</>
              ) : (
                <><CheckCircle className="mr-2 h-4 w-4" /> Import {draft?.teams.length ?? 0} teams &amp; {importGames.length} games</>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
